import { useState, useEffect } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Search, ArrowLeft, Mail } from 'lucide-react';
import { PageShell } from './layout/PageShell';

interface FAQPageProps {
  onBack: () => void;
}

const faqCategories = [
  {
    category: 'Generelt om Talentfunn',
    faqs: [
      {
        question: 'Hva er Talentfunn?',
        answer: 'Talentfunn er en plattform der brukere kan vise frem praktiske ferdigheter og bli oppdaget. Talenter bygger profil og portefølje, mens arbeidsgivere og andre brukere kan finne relevante personer, kontakte dem og samarbeide om oppdrag eller tjenester.'
      },
      {
        question: 'Hvem kan bruke Talentfunn?',
        answer: 'Alle over 15 år kan opprette konto. Brukere under 18 år plikter å informere foresatte om bruk av plattformen, og må ha samtykke fra foresatte ved kjøp av abonnement.'
      },
      {
        question: 'Hvilke brukertyper finnes?',
        answer: 'Det finnes to brukertyper:\n• Talent: Personer som viser frem ferdigheter, prosjekter og erfaring\n• Arbeidsgiver: Bedrifter som søker kandidater, kontakter talenter og kan publisere oppdrag (med riktig abonnement)'
      },
      {
        question: 'Hvilke ferdigheter passer på Talentfunn?',
        answer: 'Talentfunn er for alle typer ferdigheter. Du kan vise praktiske, kreative og digitale ferdigheter gjennom profil, prosjekter og tjenester.'
      }
    ]
  },
  {
    category: 'Registrering og konto',
    faqs: [
      {
        question: 'Hvordan oppretter jeg en konto?',
        answer: '1. Klikk på "Registrer deg" eller "Kom i gang"\n2. Velg brukertype (Talent eller Arbeidsgiver)\n3. Oppgi e-postadresse, passord og brukernavn\n4. Bekreft e-postadressen din via lenken du mottar på e-post\n5. Fullfør profilen din'
      },
      {
        question: 'Må jeg verifisere e-postadressen min?',
        answer: 'Ja, alle nye brukere må verifisere e-postadressen sin ved å klikke på lenken de mottar på e-post etter registrering.'
      },
      {
        question: 'Hva er kravet for arbeidsgivere?',
        answer: 'Arbeidsgivere må:\n• Oppgi gyldig organisasjonsnummer (verifiseres mot Brønnøysundregisteret)\n• Verifisere e-postadressen sin\n• Oppgi sin rolle i bedriften\n• Vente på manuell godkjenning fra Talentfunn\n\nUnder godkjenning kan du utforske plattformen, men ikke sende meldinger, kjøpe abonnement eller lagre favoritter.'
      }
    ]
  },
  {
    category: 'Profil og portefølje',
    faqs: [
      {
        question: 'Hvordan redigerer jeg profilen min?',
        answer: 'Gå til Profil → Rediger profil. Der kan du endre:\n• Brukernavn\n• Om meg (bio)\n• Jobbønsker (for Talent)\n• Ferdigheter\n• Fylke og by/kommune\n\nProfilbilde og showcase-bilde endres på profilsiden ved å trykke på bildet.\n\nArbeidsgivere redigerer blant annet bedriftsnavn, om bedriften, bransje og nettside.'
      },
      {
        question: 'Hva er porteføljeprosjekter?',
        answer: 'Porteføljeprosjekter er arbeider du har gjort som viser frem dine ferdigheter. Hvert prosjekt kan inneholde:\n• Tittel og beskrivelse\n• Bilder (for alle brukere)\n• Video (kun for Pluss-brukere)\n• Lenker til prosjektet'
      },
      {
        question: 'Hvor mange prosjekter kan jeg ha?',
        answer: '• Gratis-brukere: Maks 1 prosjekt\n• Pluss-brukere: 6 prosjekter'
      },
      {
        question: 'Kan jeg ha både bilder og video i samme prosjekt?',
        answer: 'Ja! Pluss-brukere kan laste opp både bilder og video i samme porteføljeprosjekt.'
      }
    ]
  },
  {
    category: 'Søk og favoritter',
    faqs: [
      {
        question: 'Hvordan søker jeg etter talenter?',
        answer: 'Bruk søkefeltet på Hjem-siden. Du kan søke på ferdigheter (f.eks. "grafisk design", "React", "snekker") og filtrere på fylke.'
      },
      {
        question: 'Hva er favoritter?',
        answer: 'Favoritter lar deg lagre talentprofiler du er interessert i. Dette er tilgjengelig for Arbeidsgiver Basis (490 kr/mnd) og Arbeidsgiver Pluss (990 kr/mnd).'
      },
      {
        question: 'Hva er lagrede søk?',
        answer: 'Med Arbeidsgiver Pluss-abonnement kan du lagre søk slik at du får varsling når det dukker opp talenter med ferdigheter du søker etter.'
      }
    ]
  },
  {
    category: 'Meldinger',
    faqs: [
      {
        question: 'Hvordan fungerer meldingssystemet?',
        answer: 'Talentfunn har et internt meldingssystem:\n• Kun Talentfunn Pluss, Arbeidsgiver Basis og Arbeidsgiver Pluss kan starte nye samtaler\n• Alle innloggede brukere kan svare i eksisterende samtaler\n• Samtaler håndteres i Meldinger-siden'
      },
      {
        question: 'Kan jeg sende vedlegg i meldinger?',
        answer: 'Ja, du kan sende bilder i meldinger (JPEG, PNG, GIF, WebP).'
      },
      {
        question: 'Får jeg varsel når jeg mottar melding?',
        answer: 'Ja! Du kan motta varsler på to måter:\n• In-app varsler: Vises når du er innlogget (alltid aktivert)\n• Push-notifikasjoner: Vises på enheten din selv når appen ikke er åpen (krever tillatelse)\n\nDu kan administrere varslingsinnstillingene i Innstillinger → Varsler.'
      }
    ]
  },
  {
    category: 'Oppdrag',
    faqs: [
      {
        question: 'Hva er Oppdrag?',
        answer: 'Oppdrag er en funksjon der arbeidsgivere kan publisere oppdrag. Andre brukere kan bla gjennom oppdrag og vise interesse ved å sende en kort melding.'
      },
      {
        question: 'Hvem kan publisere oppdrag?',
        answer: 'Kun brukere med Arbeidsgiver Pluss-abonnement (990 kr/mnd) kan publisere oppdrag. Dette sikrer kvalitet og seriøsitet i oppdragene som legges ut.'
      },
      {
        question: 'Hvem kan vise interesse for oppdrag?',
        answer: 'Alle brukere med riktig abonnement kan vise interesse for oppdrag:\n• Talentfunn Pluss (149 kr/mnd)\n• Arbeidsgiver Basis (490 kr/mnd)\n• Arbeidsgiver Pluss (990 kr/mnd)\n\nGratis-brukere kan se oppdrag, men må oppgradere for å vise interesse.'
      },
      {
        question: 'Hvordan publiserer jeg et oppdrag?',
        answer: 'Som Arbeidsgiver Pluss-bruker:\n1. Gå til Oppdrag-siden\n2. Klikk på "Publiser oppdrag"\n3. Fyll ut tittel, beskrivelse, ferdigheter, lokasjon og budsjett\n4. Velg evt. utløpsdato (valgfritt)\n5. Publiser\n\nDu kan lukke oppdraget når du vil.'
      },
      {
        question: 'Hvordan viser jeg interesse for et oppdrag?',
        answer: 'Som bruker med Talentfunn Pluss, Arbeidsgiver Basis eller Arbeidsgiver Pluss:\n1. Bla gjennom oppdrag på "Oppdrag"-fanen\n2. Klikk på et oppdrag for å se detaljer\n3. Klikk "Vis interesse"\n4. Skriv en kort pitch-melding (maks 200 tegn)\n5. Send - arbeidsgiver ser din interesse og kan starte samtale'
      },
      {
        question: 'Hvordan ser jeg mine publiserte oppdrag?',
        answer: 'Gå til Profil → Mine oppdrag. Her kan du se alle dine oppdrag, redigere dem, se interesser fra andre brukere, og lukke oppdrag.'
      },
      {
        question: 'Hvordan ser jeg oppdrag jeg har vist interesse for?',
        answer: 'Gå til aktuelle oppdrag. Der ser du oppdragene du har vist interesse for, og om arbeidsgiver har startet kontakt.'
      },
      {
        question: 'Hva skjer når jeg viser interesse for et oppdrag?',
        answer: 'Arbeidsgiver ser din interesse sammen med din pitch-melding og profil. Hvis arbeidsgiver er interessert, kan de starte en samtale med deg direkte fra interesselisten. Samtalen vises i Meldinger-siden med kontekst om oppdraget.'
      },
      {
        question: 'Kan jeg trekke tilbake min interesse?',
        answer: 'Ja. Gå til aktuelle oppdrag, og klikk "Trekk tilbake" på oppdraget du ikke lenger er interessert i.'
      }
    ]
  },
  {
    category: 'Abonnementer og priser',
    faqs: [
      {
        question: 'Hva koster Talentfunn?',
        answer: 'For Talent:\n• Gratis: Grunnleggende funksjonalitet (maks 1 offentlig prosjekt), kan svare i eksisterende samtaler\n• Pluss (149 kr/mnd): Opptil 6 prosjekter, video i prosjekter, vis interesse for oppdrag, start nye samtaler, publiser tjenester, høyere synlighet og prioritert support\n\nFor Arbeidsgiver:\n• Basis (490 kr/mnd): Start nye meldinger til talenter, vis interesse for oppdrag og bruk favoritter\n• Pluss (990 kr/mnd): Alt i Basis, pluss publisering av oppdrag, lagrede søk med varsling og prioritert support\n\nTjenesteforespørsler er tilgjengelig for alle innloggede brukere.'
      },
      {
        question: 'Hvordan betaler jeg?',
        answer: 'Alle betalinger håndteres sikkert via Nets/Nexi. Vi lagrer aldri kortinformasjon på våre servere. Abonnementer fornyes månedlig.'
      },
      {
        question: 'Er arbeidsgiver-abonnement kun for bedrifter?',
        answer: 'Ja. Arbeidsgiver-abonnementer er kun tilgjengelig som bedriftskjøp (B2B). Du må oppgi organisasjonsnummer til betalingsleverandøren ved kjøp for korrekt MVA-håndtering og fakturering.'
      },
      {
        question: 'Har jeg angrefrist?',
        answer: '• Forbrukere (privatpersoner): Talentfunn Pluss er en digital tjeneste som leveres umiddelbart. I henhold til Angrerettloven § 22 bokstav n bortfaller angreretten når du samtykker til umiddelbar levering. Du kan likevel si opp abonnementet når som helst.\n• Næringskjøp (bedrifter): Ingen lovfestet angrefrist (alle arbeidsgiver-abonnementer er B2B)\n\nSe "Vilkår og betingelser" for full informasjon.'
      },
      {
        question: 'Hvordan kansellerer jeg abonnementet mitt?',
        answer: 'Gå til Abonnement → Kanseller abonnement. Endringen trer i kraft ved slutten av inneværende faktureringsperiode. Du beholder tilgang inntil perioden utløper.'
      },
      {
        question: 'Hva skjer hvis betalingen feiler (f.eks. utløpt kort)?',
        answer: 'Ved mislykket trekk (f.eks. utløpt kort) får du varsel og beholder full tilgang i inntil 7 dager for å oppdatere betalingsdetaljene. Oppdater betalingsmetoden under Abonnement før fristen utløper; ellers avsluttes abonnementet og du må tegne på nytt.'
      },
      {
        question: 'Hva skjer med dataene mine når jeg nedgraderer eller kansellerer?',
        answer: 'Dataene dine beholdes ved nedgradering.\n\n• Talent: Du ser fortsatt alle egne prosjekter, men kun 1 publisert prosjekt vises offentlig. Du kan velge hvilket prosjekt som skal vises. Eksisterende video beholdes, men du kan ikke legge til eller endre video uten Pluss. Aktive tjenester avpubliseres automatisk, og du kan ikke publisere dem igjen før du har et aktivt Talentfunn Pluss-abonnement. Du kan ikke starte nye samtaler uten Pluss, men du kan svare i eksisterende samtaler.\n• Arbeidsgiver: Ved nedgradering til gratis kan du ikke starte nye meldinger, og favoritter/lagrede søk skjules (kan gjenopprettes ved oppgradering). Ved nedgradering fra Arbeidsgiver Pluss til Basis beholder du meldinger og favoritter, men mister lagrede søk.\n\nDu får varsel før perioden utløper, og kan oppgradere igjen når som helst.'
      },
      {
        question: 'Hvordan skaffe jobb med praktiske ferdigheter?',
        answer: 'Registrer deg som talent på talentfunn.no, fyll ut profilen med ferdigheter og portefølje, og publiser prosjekter som viser hva du kan. Arbeidsgivere og andre brukere kan finne deg gjennom søk og profiler. Med Talentfunn Pluss kan du i tillegg vise interesse for oppdrag, starte nye samtaler og publisere tjenester.'
      },
      {
        question: 'Tilbyr dere refusjon?',
        answer: 'Vi tilbyr ikke refusjon for delvis brukte perioder. Angreretten bortfaller for Pluss-abonnement ved umiddelbar levering (se over).'
      }
    ]
  },
  {
    category: 'Video og bilder',
    faqs: [
      {
        question: 'Hvilke filformater støttes?',
        answer: 'Bilder: JPG, PNG, GIF, WebP\nVideo: MP4, MOV, WebM, AVI'
      },
      {
        question: 'Hvor store kan filene være?',
        answer: '• Bilder: Maksimalt 10 MB per bilde\n• Video: Maksimalt 150 MB per video'
      },
      {
        question: 'Hvor lagres videoene mine?',
        answer: 'Videoer lagres og streames via Bunny.net (servere i EU). Merk at videoer kan være offentlig synlige når du har samtykket til dette ved opplasting.'
      },
      {
        question: 'Slettes videoene mine hvis jeg sletter dem?',
        answer: 'Ja! Når du sletter en video fra Talentfunn, slettes den også fra Bunny.net innen 24 timer. Ved kontosletting slettes alle dine videoer permanent.'
      }
    ]
  },
  {
    category: 'Personvern og sikkerhet',
    faqs: [
      {
        question: 'Hvordan beskyttes mine personopplysninger?',
        answer: 'Vi følger GDPR og norsk personvernlovgivning. Les full informasjon i vår Personvernerklæring.'
      },
      {
        question: 'Kan jeg skjule online-status?',
        answer: 'Ja! Gå til Innstillinger → Personvern → Skjul «Aktiv nå» status. Da vil andre brukere ikke se når du er aktiv på plattformen.'
      },
      {
        question: 'Hvordan eksporterer jeg mine data?',
        answer: 'Gå til Innstillinger → Mine Data → Last ned mine data. Dataene lastes ned direkte som en lesbar rapport (HTML) og en maskinlesbar JSON-fil.'
      },
      {
        question: 'Hvordan sletter jeg kontoen min?',
        answer: 'Gå til Innstillinger → Mine Data → Slett kontoen min. Dette sletter permanent:\n• Profilen din\n• Alle porteføljeprosjekter\n• Alle meldinger\n• Alle bilder og videoer\n\nAdvarsel: Denne handlingen kan ikke angres!'
      }
    ]
  },
  {
    category: 'Teknisk støtte',
    faqs: [
      {
        question: 'Fungerer Talentfunn på mobil?',
        answer: 'Ja! Talentfunn er en Progressive Web App (PWA) som fungerer på alle enheter - mobil, nettbrett og desktop.'
      },
      {
        question: 'Kan jeg installere Talentfunn som app?',
        answer: 'Ja! I de fleste nettlesere (Chrome, Safari, Edge) vil du få et spørsmål om å installere Talentfunn på enheten din. Dette gir deg:\n• App-ikon på hjemmeskjermen\n• Kjører i fullt vindu (uten nettleserens adressefelt)\n• Raskere lasting\n• Offline-støtte (kommer snart)'
      },
      {
        question: 'Hvilke nettlesere støttes?',
        answer: 'Talentfunn fungerer best på nyere versjoner av Chrome, Safari, Edge og Firefox.'
      },
      {
        question: 'Jeg har glemt passordet mitt. Hva gjør jeg?',
        answer: 'Klikk på "Glemt passord?" på innloggingssiden, oppgi e-postadressen din, og du vil motta en lenke for å tilbakestille passordet.'
      },
      {
        question: 'Jeg har ikke mottatt verifiserings-e-posten. Hva gjør jeg?',
        answer: '1. Sjekk spam/søppelpost-mappen\n2. Vent noen minutter (e-post kan bli forsinket)\n3. Be om ny verifiserings-e-post i innstillingene\n4. Kontakt support hvis problemet vedvarer: kontakt@talentfunn.no'
      }
    ]
  },
  {
    category: 'Retningslinjer og regler',
    faqs: [
      {
        question: 'Hva slags innhold er forbudt?',
        answer: 'Talentfunn har nulltoleranse for:\n• Nakenhet, pornografi eller seksuelt innhold\n• Vold eller oppfordring til vold\n• Hatefulle ytringer eller trakassering\n• Spam eller uønsket markedsføring\n• Ulovlig innhold\n• Innhold som krenker andres rettigheter\n\nBrudd fører til umiddelbar og permanent utestengelse.'
      },
      {
        question: 'Kan jeg bruke bilder/video jeg ikke eier rettighetene til?',
        answer: 'Nei. Du er alene ansvarlig for alt innhold du publiserer. Du må ha rettigheter til alt materiale du laster opp.'
      },
      {
        question: 'Hva gjør jeg hvis jeg ser upassende innhold?',
        answer: 'Rapporter det umiddelbart til kontakt@talentfunn.no. Vi tar alle rapporter på alvor og vil undersøke raskt.'
      }
    ]
  },
  {
    category: 'Kontakt og support',
    faqs: [
      {
        question: 'Hvordan kontakter jeg support?',
        answer: 'E-post: kontakt@talentfunn.no'
      },
      {
        question: 'Hvor raskt får jeg svar?',
        answer: 'Vi svarer så raskt som mulig. Pluss-brukere prioriteres i supportkøen.'
      },
      {
        question: 'Hvor finner jeg mer informasjon?',
        answer: '• Vilkår og betingelser: Se fullstendige vilkår i appen\n• Personvernerklæring: Se vår personvernpolicy i appen\n• Om Talentfunn: Les mer om oss i "Om Talentfunn"-siden'
      }
    ]
  }
];

export function FAQPage({ onBack }: FAQPageProps) {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const toggleFAQ = (id: string) => {
    setOpenIndex(openIndex === id ? null : id);
  };

  // Flatten all FAQs for searching
  const allFaqs = faqCategories.flatMap((cat, catIndex) =>
    cat.faqs.map((faq, faqIndex) => ({
      ...faq,
      category: cat.category,
      id: `${catIndex}-${faqIndex}`
    }))
  );

  const filteredFaqs = searchQuery
    ? allFaqs.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50">
      {/* Content - scroll handled by main / App wrapper */}
      <PageShell className="pt-page-safe-sm xl:w-[56rem] xl:max-w-[56rem] xl:mx-auto xl:px-4 xl:px-8 xl:pt-5 xl:pb-24">
          {/* Content container - fullscreen on mobile, rounded modal on desktop */}
          <div className="pt-5 xl:w-full xl:bg-white xl:rounded-3xl xl:shadow-xl xl:border-2 xl:border-purple-100 xl:p-8">
            {/* Back button */}
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 px-4 xl:px-0 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Tilbake</span>
            </button>

            {/* Header */}
            <h1 className="text-3xl sm:text-4xl mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1 px-4 xl:px-0">
              Ofte stilte spørsmål
            </h1>
            <p className="text-gray-600 mb-8 px-4 xl:px-0">
              Finn svar på vanlige spørsmål om Talentfunn
            </p>

            {/* Search */}
            <div className="relative mb-8 px-4 xl:px-0">
              <input
                type="text"
                placeholder="Søk i FAQ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 border-2 border-purple-100 rounded-2xl focus:outline-none focus:border-purple-300"
              />
              <Search className="absolute right-4 top-4 w-5 h-5 text-gray-500" />
            </div>

            {/* Display filtered results or categorized */}
            {filteredFaqs ? (
              <div className="space-y-4 px-4 xl:px-0">
                {filteredFaqs.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">
                    Ingen resultater funnet
                  </p>
                ) : (
                  filteredFaqs.map((faq) => (
                    <div
                      key={faq.id}
                      className="border-2 border-purple-100 rounded-2xl overflow-hidden transition-all hover:border-purple-300"
                    >
                      <button
                        onClick={() => toggleFAQ(faq.id)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-purple-50 transition-colors"
                      >
                        <div className="pr-4">
                          <div className="text-xs text-purple-600 mb-1">{faq.category}</div>
                          <div className="text-purple-900">{faq.question}</div>
                        </div>
                        {openIndex === faq.id ? (
                          <ChevronUp className="w-5 h-5 text-purple-600 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-purple-600 flex-shrink-0" />
                        )}
                      </button>
                      {openIndex === faq.id && (
                        <div className="px-6 pb-4 pt-2 bg-purple-50 border-t-2 border-purple-100">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line break-words">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            ) : (
              // Show categorized view
              <div className="space-y-8 px-4 xl:px-0">
                {faqCategories.map((category, catIndex) => (
                  <div key={catIndex}>
                    <h2 className="text-xl text-purple-900 mb-4 pb-2 border-b-2 border-purple-100">
                      {category.category}
                    </h2>
                    <div className="space-y-4">
                      {category.faqs.map((faq, faqIndex) => {
                        const id = `${catIndex}-${faqIndex}`;
                        return (
                          <div
                            key={id}
                            className="border-2 border-purple-100 rounded-2xl overflow-hidden transition-all hover:border-purple-300"
                          >
                            <button
                              onClick={() => toggleFAQ(id)}
                              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-purple-50 transition-colors"
                            >
                              <span className="text-purple-900 pr-4 break-words">{faq.question}</span>
                              {openIndex === id ? (
                                <ChevronUp className="w-5 h-5 text-purple-600 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-purple-600 flex-shrink-0" />
                              )}
                            </button>
                            {openIndex === id && (
                              <div className="px-6 pb-4 pt-2 bg-purple-50 border-t-2 border-purple-100">
                                <p className="text-gray-700 leading-relaxed whitespace-pre-line break-words">{faq.answer}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Contact Section */}
            <div className="mt-8 mx-4 xl:mx-0 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200">
              <h3 className="text-xl text-purple-900 mb-2">Fant du ikke svar?</h3>
              <p className="text-gray-700 mb-4">
                Hvis du har andre spørsmål, ta gjerne kontakt med oss!
              </p>
              <div className="flex items-center gap-2 text-purple-600">
                <Mail className="w-5 h-5" />
                <a href="mailto:kontakt@talentfunn.no" className="hover:underline">
                  kontakt@talentfunn.no
                </a>
              </div>
              <p className="text-gray-600 text-sm mt-2">
                Pluss-brukere får prioritert support (svar innen 1 virkedag)
              </p>
            </div>

            {/* Last updated */}
            <p className="text-gray-500 text-sm text-center mt-6 px-4 xl:px-0">
              Sist oppdatert: 2. mars 2026
            </p>
          </div>
        </PageShell>
    </div>
  );
}
